import React, { FC } from "react"
import L10n from "lib/carrot-l10n"
import { PageHeader, ResponsiveWrapper } from "../molecules/Molecules"
import { ButtonContents, TalkToCarrotButton } from "../talktocarrot/TalkToCarrotButton"
import { BodyLayout } from "../atoms/layouts/BodyLayout"
import { Spacer } from "../atoms/Atoms"
import { Button } from "../atoms/buttons/Button"
import { ButtonPrimary } from "../atoms/buttons/ButtonPrimary"
import { MasterLayout } from "../atoms/layouts/MasterLayout"
import { Body } from "../atoms/Bodies"
import { useSelector } from "react-redux"
import { getHasAnUpcomingOrCompletedExpertChat, getHasMetSignatureRequirement } from "reducers/userInfo"
import { useToggle } from "utils/Hooks"
import { Modal } from "../modal/Modal"
import Settings from "../../../utils/CarrotConfig"
import { H2Library } from "@carrotfertility/carotene"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

type BackButtonsProps = {
  backOnClick: () => void
  doThisLaterOnClick: () => void
}

const BackButtons: FC<BackButtonsProps> = ({ backOnClick, doThisLaterOnClick }: BackButtonsProps) => {
  return (
    <div className="flex flex-row justify-end">
      <div onClick={backOnClick} className="mr3">
        <Button outline className="min-w3 tc">
          {l`Back`}
        </Button>
      </div>
      <ButtonPrimary className="min-w3 tc" onClick={doThisLaterOnClick}>
        {l`Do this later`}
      </ButtonPrimary>
    </div>
  )
}

type DocusignModalProps = {
  onClose: () => void
}

const DocusignModal: FC<DocusignModalProps> = ({ onClose }) => {
  return (
    <Modal titleText={l`Authorization Form Modal`} onExit={onClose} fullscreen={true}>
      <div className="tr ph35-s pb35-s ph45-ns pb45-ns w-100 h-100">
        <iframe
          className="w-100 h-100 border-box ba b--black-30"
          title={l`Authorization Form`}
          src={`/signature`}
          frameBorder="0"
          allowFullScreen
        />
      </div>
    </Modal>
  )
}

type SignAuthorizationFormButtonProps = {
  iconName?: string
  label: string
  onClick: () => void
  enabled?: boolean
}

const SignAuthorizationFormButton: FC<SignAuthorizationFormButtonProps> = ({
  iconName,
  label,
  onClick,
  enabled = true
}: SignAuthorizationFormButtonProps) => {
  const cursorType = enabled ? "pointer" : "not-allowed"
  const buttonOpacity = enabled ? "" : "o-30"
  const onClickHandler = enabled ? onClick : null

  return (
    <div className={buttonOpacity}>
      <a onClick={onClickHandler} className={`no-underline ${cursorType}`}>
        <ButtonContents label={label} iconName={iconName} enabled={enabled} />
      </a>
    </div>
  )
}

type SignAuthorizationFormModalButtonProps = {
  enabled: boolean
}

const SignAuthorizationFormModalButton: FC<SignAuthorizationFormModalButtonProps> = ({ enabled }) => {
  const [isModalOpen, toggleIsModalOpen] = useToggle(false)

  return (
    <>
      <SignAuthorizationFormButton
        enabled={enabled}
        iconName="icn-documents"
        label={l`Sign required documents`}
        onClick={() => {
          toggleIsModalOpen()
        }}
      />
      {isModalOpen && <DocusignModal onClose={toggleIsModalOpen} />}
    </>
  )
}

type IvfRequirementsProps = {
  backOnClick: () => void
  doThisLaterOnClick: () => void
}

const RequirementsButtons = () => {
  const hasAnUpcomingOrCompletedExpertChat = useSelector(getHasAnUpcomingOrCompletedExpertChat)
  const hasMetSignatureRequirement = useSelector(getHasMetSignatureRequirement)
  return (
    <>
      <TalkToCarrotButton
        enabled={!hasAnUpcomingOrCompletedExpertChat}
        useModal={true}
        to={Settings.IVF_TREATMENT_OPTIONS_URL}
        iconName="icn-schedule-chat"
        label={l`Schedule expert chat`}
      />
      <SignAuthorizationFormModalButton enabled={!hasMetSignatureRequirement} />
    </>
  )
}

const RequirementsSteps = () => (
  <ResponsiveWrapper padding={0.75}>
    <Body color={"black-80"}>
      <u>{l`Step 1:`}</u>
      {l` You’re required to meet with a medical expert to discuss treatment options, learn what IVF care Carrot can cover, and complete a quick questionnaire before and after. If you’re pursuing IVF with a partner, both of you need to attend this session.`}
    </Body>
    <Body color={"black-80"}>
      <u>{l`Step 2:`}</u>{" "}
      {l` The individual(s) having the embryo transfer as part of IVF treatment must sign an authorization form allowing Carrot access to their fertility health information and confirm they agree to Carrot’s IVF reimbursement policy.`}
    </Body>
    <RequirementsButtons />
  </ResponsiveWrapper>
)

const IvfRequirements: FC<IvfRequirementsProps> = ({ backOnClick, doThisLaterOnClick }: IvfRequirementsProps) => (
  <MasterLayout header={<PageHeader title={l`Unlock your financial benefit for IVF`} icon="icn-get-reimbursed" />}>
    <BodyLayout>
      <H2Library>{l`Requirements for your IVF journey`}</H2Library>
      <Spacer height={1.5} />
      <Body color={"black-80"}>
        {l`Members and their partners on an IVF journey have additional requirements to complete to unlock their financial benefit.`}
      </Body>
      <Spacer height={2} />
      <Body weight={"b"}>
        {l`Carrot will not process your IVF-related expenses until you have completed both of these steps.`}
      </Body>
      <Spacer height={2} />
      <RequirementsSteps />
      <Spacer height={3} />
      <BackButtons backOnClick={backOnClick} doThisLaterOnClick={doThisLaterOnClick} />
      <Spacer height={1.5} />
      <div className="bb b--black-10" />
    </BodyLayout>
  </MasterLayout>
)

export { IvfRequirements }
